import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { CoreHttpClient } from "@obg/ngx.core";
import { CookieService, DocumentRef } from "@obg/ngx.global.evo";
import { Route, RouteSelector } from "@obg/ngx.router";
import { ignoreNil } from "@obg/rxjs";
import { addDays } from "date-fns";
import { isEmpty } from "lodash-es";
import { Observable, of } from "rxjs";
import { switchMap, take, tap, map } from "rxjs/operators";

import { CommonState } from "../state";

import { AffiliateConfigService } from "./affiliate.config";
import { AffiliateResponse, AffiliateUTMParams, UTM_COOKIE_NAMES } from "./affiliate.model";

export abstract class AffiliateService {
	abstract initialize(): void;

	abstract getCookieName(): string | undefined;

	abstract getCookieTokenName(): string | undefined;

	abstract getPartnerId(): string | undefined;

	abstract getReferrer(): string | undefined;
}

@Injectable()
export class ClientAffiliateService implements AffiliateService {
	codeCookieName: string;
	tokenCookieName: string;
	partnerIdCookieName: string;

	constructor(
		private config: AffiliateConfigService,
		private cookieService: CookieService,
		private httpClient: CoreHttpClient,
		private documentRef: DocumentRef,
		private store: Store<CommonState>
	) {
		this.codeCookieName = this.config.tokenCookieName();
		this.tokenCookieName = this.config.codeCookieName();
		this.partnerIdCookieName = this.config.partnerIdCookieName();
	}

	initialize() {
		if (this.config.isDisabled()) {
			return;
		}

		this.store
			.pipe(
				RouteSelector.getCurrent$,
				take(1),
				switchMap((route) =>
					this.getAffiliateData(route).pipe(
						ignoreNil(),
						take(1),
						tap((affiliateReponse) => this.setAffiliateCookies(affiliateReponse))
					)
				)
			)
			.subscribe(); // eslint-disable-line rxjs/no-ignored-subscription
	}

	/**
	 * @deprecated [fabric-deprecated] - Use document.obgEventBus.retrieve(AccountAppValue.GetAffiliateData) instead.
	 */
	getCookieName(): string | undefined {
		return this.cookieService.get(this.tokenCookieName);
	}

	/**
	 * @deprecated [fabric-deprecated] - Use document.obgEventBus.retrieve(AccountAppValue.GetAffiliateData) instead.
	 */
	getCookieTokenName(): string | undefined {
		return this.cookieService.get(this.codeCookieName);
	}

	/**
	 * @deprecated [fabric-deprecated] - Use document.obgEventBus.retrieve(AccountAppValue.GetAffiliateData) instead.
	 */
	getPartnerId(): string | undefined {
		return this.cookieService.get(this.partnerIdCookieName);
	}

	getReferrer(): string | undefined {
		const referrer = this.documentRef.native.referrer;
		return !isEmpty(referrer) ? referrer.slice(0, 255) : undefined;
	}

	private getAffiliateData(route: Route): Observable<AffiliateResponse | undefined> {
		const from = route.queryParams ? route.queryParams[this.config.fromQueryStringName()] : "";
		const affCode = route.queryParams ? route.queryParams[this.config.codeQueryStringName()] : "";
		const token = route.queryParams ? route.queryParams[this.config.tokenQueryStringName()] : "";
		/* eslint-disable @typescript-eslint/naming-convention */
		/* Reason: UTM cookie variable names use snake_case to match URL parameter keys */
		const utm_source = route.queryParams ? route.queryParams[UTM_COOKIE_NAMES.utm_source || ""] : "";
		const utm_medium = route.queryParams ? route.queryParams[UTM_COOKIE_NAMES.utm_medium || ""] : "";
		const utm_campaign = route.queryParams ? route.queryParams[UTM_COOKIE_NAMES.utm_campaign || ""] : "";
		const utm_term = route.queryParams ? route.queryParams[UTM_COOKIE_NAMES.utm_term || ""] : "";
		const utm_content = route.queryParams ? route.queryParams[UTM_COOKIE_NAMES.utm_content || ""] : "";
		/* eslint-enable @typescript-eslint/naming-convention */

		const trafficSource = route.queryParams ? route.queryParams["traffic-source"] : "";
		const decodedTrafficSource = trafficSource ? decodeURIComponent(trafficSource) : "";
		const referrer = decodedTrafficSource ? decodedTrafficSource : this.getReferrer();

		if (isEmpty(from) && isEmpty(affCode) && isEmpty(token) && isEmpty(referrer)) {
			return of(undefined);
		}

		return this.httpClient
			.get<AffiliateResponse>({
				url: this.config.getAffiliatesEndpoint(),
				searchParams: { referrer, from, affCode, token },
			})
			.pipe(
				map((response) => ({
					...response,
					utmCookiesNames: {
						utm_source,
						utm_medium,
						utm_campaign,
						utm_term,
						utm_content,
					},
				}))
			);
	}

	private setAffiliateCookies(affiliateResponse: AffiliateResponse) {
		const { partnerId, affcode, token, utmCookiesNames } = affiliateResponse;

		if (!token && !affcode && !partnerId) {
			return;
		}

		const expiryDate = addDays(new Date(), this.config.cookieExpiryInDays());

		const utmCookies = Object.entries(UTM_COOKIE_NAMES).reduce(
			(acc, [key, value]) => {
				const cookieValue = utmCookiesNames?.[key as keyof AffiliateUTMParams];
				if (cookieValue) {
					acc.push({ name: value, value: cookieValue });
				}
				return acc;
			},
			[] as { name: string; value: string }[]
		);

		const cookies = [
			...utmCookies,
			{ name: this.config.tokenCookieName(), value: token },
			{ name: this.config.codeCookieName(), value: affcode },
			{ name: this.config.partnerIdCookieName(), value: partnerId },
		];

		cookies.map((cookie) => {
			this.cookieService.delete(cookie.name);
			this.cookieService.set({
				name: cookie.name,
				value: cookie.value,
				expiryDate,
				sameSite: "none",
				secure: true,
			});
		});
	}
}

export class NoopAffiliateService implements AffiliateService {
	initialize() {
		// nothing to do here
	}

	getCookieName(): string | undefined {
		return undefined;
	}

	getCookieTokenName(): string | undefined {
		return undefined;
	}

	getPartnerId(): string | undefined {
		return undefined;
	}

	getReferrer(): string | undefined {
		return undefined;
	}
}
